/**
 * ColorSpinner is a compound widget with three spinner to change the RGB or HSV color values.
 *
 * @module mw-colorpicker/ColorSpinner
 * @mixes  dijit/_WidgetBase
 * @mixes  dijit/_TemplatedMixin
 * @mixes  dijit/_WidgetsInTemplateMixin
 *
 * @example <caption>Simple MATLAB Color Spinner</caption>
 * var colorSpinner = new ColorSpinner();
 * colorSpinner.on('change', function(e) {
 *      console.log(e.mwEventData.newValue);
 * });
 *

 * @copyright Copyright 2017 MathWorks, Inc.
 */
define([
    'dojo/_base/declare',
    'dojo/dom-style',
    'dijit/_WidgetBase',
    'dijit/_TemplatedMixin',
    'dijit/_WidgetsInTemplateMixin',
    'mw-form/ComboBox',
    'mw-form/Spinner',
    'mw-form/TextField',
    'mw-form/Label',
    './MColor',
    'dojo/text!./templates/ColorSpinner.html',
    'dojo/i18n!./l10n/nls/ColorPickerResources'
], function (declare, domStyle, _WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin,
    ComboBox, Spinner, Textfield, Label, MColor, template, l10nStrs) {
    var _doubleSpinnerProps = {
        value: 0,
        step: 0.0100,
        width: 67,
        errorText: 'Value must be between 0 and 1.',
        format: '%.2f',
        height: 22,
        horizontalAlignment: 'right',
        includeMaximum: true,
        includeMinimum: true,
        limits: {
            minimum: 0,
            maximum: 1
        },
        precisionValue: 2,
        roundValueToInt: false
    };

    var _intSpinnerProps = {
        step: 1,
        width: 67,
        errorText: 'Value must be between 0 and 255.',
        format: '%.0f',
        height: 22,
        horizontalAlignment: 'right',
        includeMaximum: true,
        includeMinimum: true,
        limits: {
            minimum: 0,
            maximum: 255
        },
        roundValueToInt: true
    };

    var _hexSpinnerProps = {
        pattern: '^#[0-9a-f]{6}',
        required: true,
        selectOnClick: true,
        intermediateChanges: true,
        width: 206,
        height: 22,
        horizontalAlignment: 'center',
        style: 'font-size: 12px;'
    };

    var _hueSpinnerProps = {
        step: 1,
        width: 67,
        errorText: 'Value must be between 0 and 360.',
        format: '%.0f',
        height: 22,
        horizontalAlignment: 'right',
        includeMaximum: true,
        includeMinimum: true,
        limits: {
            minimum: 0,
            maximum: 360
        },
        roundValueToInt: true
    };

    var _SVSpinnerProps = {
        step: 1,
        width: 67,
        errorText: 'Value must be between 0 and 100.',
        format: '%.0f',
        height: 22,
        includeMaximum: true,
        includeMinimum: true,
        limits: {
            minimum: 0,
            maximum: 100
        },
        roundValueToInt: true
    };

    return declare([_WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin], {

        /**
         * @property {string} value - Hex value of the current color value
         * @instance
         */
        value: '#000000',

        baseClass: 'mwColorSpinner',

        templateString: template,

        _redTitle: l10nStrs.redTitle,
        _greenTitle: l10nStrs.greenTitle,
        _blueTitle: l10nStrs.blueTitle,
        _hexTitle: l10nStrs.hexTitle,
        _hueTitle: l10nStrs.hueTitle,
        _saturationTitle: l10nStrs.saturationTitle,
        _valueTitle: l10nStrs.valueTitle,

        _DOUBLE_LABEL: l10nStrs.doubleLabel,
        _INT_LABEL: l10nStrs.intLabel,
        _HEX_LABEL: l10nStrs.hexLabel,
        _HSV_LABEL: l10nStrs.hsvLabel,

        _handleChangeFcn: {},

        postCreate: function () {
            this.inherited(arguments);

            // Update ComboBox properties
            this.comboBox.set('items',
                [{
                    label: this._DOUBLE_LABEL,
                    value: this._DOUBLE_LABEL
                }, {
                    label: this._INT_LABEL,
                    value: this._INT_LABEL
                }, {
                    label: this._HEX_LABEL,
                    value: this._HEX_LABEL
                }, {
                    label: this._HSV_LABEL,
                    value: this._HSV_LABEL
                }]);
            this.comboBox.set('value', this._DOUBLE_LABEL);
            this.comboBox.set('width', 206);
            this.comboBox.set('blurAfterSelection', false); // TODO: Remove after the default setting in ComboBox is changed to false (19a plan)

            this.own(
                this.comboBox.on('change', this._changeColorModel.bind(this)),
                this.spinnerNode1.on('change', this._handleSpinnerChange.bind(this)),
                this.spinnerNode2.on('change', this._handleSpinnerChange.bind(this)),
                this.spinnerNode3.on('change', this._handleSpinnerChange.bind(this)),
                this.hexTextField.on('change', this._handleHexChange.bind(this))
            );
            this._handleChangeFcn = this._handleDoubleSpinnerChange.bind(this);
            this._updateSpinnerValues(new MColor(this.value));
            this._setLabelsToRGB();
            this._setSpinnerProps(_doubleSpinnerProps);
            this.hexTextField.set(_hexSpinnerProps);
        },

        _setValueAttr: function (newColor) {
            var mcolor = new MColor(newColor);
            this._set('value', mcolor.toHex());
            this._updateSpinnerValues(mcolor);
        },

        // Handle a selection of a different color representation from the ComboBox
        _changeColorModel: function (e) {
            e.stopPropagation();
            var model = this.comboBox.get('value');
            if (model === this._DOUBLE_LABEL) {
                this._setSpinnerProps(_doubleSpinnerProps);
                this._setLabelsToRGB();
                this._handleChangeFcn = this._handleDoubleSpinnerChange.bind(this);
            } else if (model === this._INT_LABEL) {
                this._setSpinnerProps(_intSpinnerProps);
                this._setLabelsToRGB();
                this._handleChangeFcn = this._handleIntSpinnerChange.bind(this);
            } else if (model === this._HEX_LABEL) {
                this.hexTextField.set('value', this.get('value'));
                this._showHex();
            } else if (model === this._HSV_LABEL) {
                this._hideHex();
                this._setLabelsToHSV();
                this.spinnerNode1.set(_hueSpinnerProps);
                this.spinnerNode2.set(_SVSpinnerProps);
                this.spinnerNode3.set(_SVSpinnerProps);
                this._handleChangeFcn = this._handleHsvSpinnerChange.bind(this);
            }
            this._updateSpinnerValues(new MColor(this.get('value')));
        },

        /* Methods that update spinner or textfield values.
           These methods will be called from either a combobox selection
           or from programmatically setting the ColorSpinner value */

        _updateSpinnerValues: function (mcolor) {
            var colorModel = this.comboBox.get('value');

            if (colorModel === this._DOUBLE_LABEL) {
                this._updateDoubleValues(mcolor);
            } else if (colorModel === this._INT_LABEL) {
                this._updateIntValues(mcolor);
            } else if (colorModel === this._HEX_LABEL) {
                this.hexTextField.set('value', mcolor.toHex());
                this.hexTextField.set('validationState', 'normal');
            } else if (colorModel === this._HSV_LABEL) {
                this._updateHsvValues(mcolor);
            }
        },

        _updateDoubleValues: function (mcolor) {
            var cDouble = mcolor.toDoubleRGB();
            cDouble = cDouble.map(function (e) {
                return Math.round(e * 1e2) / 1e2;
            });
            this._setSpinnerValues(cDouble);
        },

        _updateIntValues: function (mcolor) {
            var cInt = mcolor.toRgb();
            this._setSpinnerValues(cInt);
        },

        _updateHsvValues: function (mcolor) {
            var rgb = mcolor.toRgb();
            var hsv = MColor.toHSV([rgb[0], rgb[1], rgb[2]]);
            this._setSpinnerValues(hsv);
        },

        /* Methods that emit change events and update this.value due to
           user input or interaction with the spinners or Hex textfield */

        _handleSpinnerChange: function (e) {
            e.stopPropagation();

            var s1 = this.spinnerNode1.get('value');
            var s2 = this.spinnerNode2.get('value');
            var s3 = this.spinnerNode3.get('value');
            var spinnerValues = [s1, s2, s3];
            this._handleChangeFcn(spinnerValues);
        },

        _handleDoubleSpinnerChange: function (spinnerValues) {
            var newColor = MColor.fromDoubleRGB(spinnerValues);
            this._setValueAndEmitChange(newColor.toHex());
        },

        _handleIntSpinnerChange: function (spinnerValues) {
            var roundedSpinnerValues = spinnerValues.map(Math.round);
            var newColor = new MColor(roundedSpinnerValues);
            this._setValueAndEmitChange(newColor.toHex());
        },

        _handleHsvSpinnerChange: function (spinnerValues) {
            var roundedSpinnerValues = spinnerValues.map(Math.round);
            var h = roundedSpinnerValues[0];
            var s = roundedSpinnerValues[1];
            var v = roundedSpinnerValues[2];
            var rgbColor = MColor.fromHSV([h, s, v]);
            var newColor = new MColor(rgbColor);
            this._setValueAndEmitChange(newColor.toHex());
        },

        _handleHexChange: function (e) {
            e.stopPropagation();
            var newHexValue = this._parseAndGetHexValue();

            if (newHexValue) {
                this.hexTextField.set('value', newHexValue);
                this._setValueAndEmitChange(newHexValue);
            }
        },

        /* Other helper methods */

        _parseAndGetHexValue: function () {
            var newHexValue = this.hexTextField.get('value');

            if (/^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(newHexValue)) {
                this.hexTextField.set('validationState', 'normal');
                var newHexColor = new MColor(newHexValue);
                return newHexColor.toHex();
            } else {
                this.hexTextField.set('validationState', 'error');
                this.hexTextField.set('errorText', l10nStrs.invalidHexErrorMessage);
                return null;
            }
        },
        _setSpinnerValues: function (values) {
            this.spinnerNode1.set('value', values[0]);
            this.spinnerNode2.set('value', values[1]);
            this.spinnerNode3.set('value', values[2]);
        },

        _setSpinnerProps: function (props) {
            this._hideHex();
            this.spinnerNode1.set(props);
            this.spinnerNode2.set(props);
            this.spinnerNode3.set(props);
        },

        _showHex: function () {
            domStyle.set(this.hexNode, 'display', '');
            domStyle.set(this.spinnersNode, 'display', 'none');
        },

        _hideHex: function () {
            domStyle.set(this.hexNode, 'display', 'none');
            domStyle.set(this.spinnersNode, 'display', '');
        },

        _setLabelsToHSV: function () {
            this.titleNode1.set('text', this._hueTitle);
            this.titleNode2.set('text', this._saturationTitle);
            this.titleNode3.set('text', this._valueTitle);
        },

        _setLabelsToRGB: function () {
            this.titleNode1.set('text', this._redTitle);
            this.titleNode2.set('text', this._greenTitle);
            this.titleNode3.set('text', this._blueTitle);
        },

        _setValueAndEmitChange: function (newHexValue) {
            var oldValue = this.get('value');
            if (oldValue !== newHexValue) {
                this._set('value', newHexValue);
                this.emit('change', {
                    mwEventData: {
                        propertyName: 'value',
                        oldValue: oldValue,
                        newValue: newHexValue
                    }
                });
            }
        }
    });
});
